/**
 * A version's TRAININGS — the training PDFs and videos filed with
 * `purpose: "training"` (CEO, 2026-09-17).
 *
 * The section sits below Documentation in the version pane and deliberately
 * lists a different set: portal-api's release documents endpoint returns
 * documentation by default and trainings only when asked
 * (`listReleaseTrainings` → `?purpose=training`), so neither section ever
 * shows the other's rows. Resellers read these on their own Trainings page;
 * customers never do. That decision is portal-api's resolver, not this list.
 *
 * Like documentation, a training belongs to the VERSION: nothing here writes
 * at the release level.
 */
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FileText, GraduationCap, SquarePlay } from "lucide-react";
import { useState, type JSX } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { DocSummary } from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "../../../lib/api.js";
import { listReleaseTrainings } from "../../../pages/documentation/api.js";
import { ReleaseTrainingDialog } from "./ReleaseTrainingDialog.js";
import type { ReleaseDTO } from "./releases-api.js";

/** What a training reads as on its chip. A training file is a PDF (filed as
 *  a `guide`) or a video; anything else keeps its document type's name. */
export function trainingTypeLabel(doc: Pick<DocSummary, "doc_type">): string {
  switch (doc.doc_type) {
    case "video":
      return "Video";
    case "guide":
    case "file":
      return "PDF";
    case "slide":
      return "Slides";
    case "faq":
      return "FAQ";
    default:
      return "Article";
  }
}

interface Props {
  release: ReleaseDTO;
  /** May add trainings — the Documentation authoring tiers, as for documents. */
  canAuthorDocs: boolean;
}

export function ReleaseTrainings({ release, canAuthorDocs }: Props): JSX.Element {
  const [adding, setAdding] = useState(false);

  const query = useQuery({
    queryKey: ["admin", "release-trainings", release.id],
    queryFn: () => listReleaseTrainings(release.id),
  });
  const trainings: DocSummary[] = query.data ?? [];

  return (
    <Box sx={{ display: "grid", gap: 1 }} data-testid={`release-${release.version}-trainings`}>
      {query.isLoading ? (
        <PageLoader />
      ) : query.isError ? (
        <Alert
          severity="error"
          sx={{ fontSize: 13 }}
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          {query.error instanceof ApiError && query.error.message
            ? query.error.message
            : "Could not load this version's trainings."}
        </Alert>
      ) : trainings.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          No trainings for {release.version} yet
          {canAuthorDocs ? " — add a training PDF or video below." : "."}
        </Typography>
      ) : (
        trainings.map((doc) => <TrainingRow key={doc.id} doc={doc} version={release.version} />)
      )}

      {canAuthorDocs && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<GraduationCap size={14} />}
            onClick={() => setAdding(true)}
            aria-label={`Add training to ${release.version}`}
          >
            Add training
          </Button>
          {adding && (
            <ReleaseTrainingDialog
              releaseId={release.id}
              version={release.version}
              open
              onClose={() => setAdding(false)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function TrainingRow({ doc, version }: { doc: DocSummary; version: string }): JSX.Element {
  return (
    <Box
      data-testid={`release-${version}-training-${doc.id}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        opacity: doc.status === "published" ? 1 : 0.7,
      }}
    >
      <Box sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0 }}>
        {doc.doc_type === "video" ? <SquarePlay size={18} /> : <FileText size={18} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component={RouterLink}
            to={`/documentation/library/${encodeURIComponent(doc.slug)}`}
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "text.primary",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {doc.title}
          </Typography>
          <Chip label={trainingTypeLabel(doc)} size="small" variant="outlined" sx={{ height: 18 }} />
          {doc.status !== "published" && (
            <Chip
              label={doc.status === "draft" ? "Draft" : doc.status}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18 }}
            />
          )}
        </Box>
        {doc.summary && (
          <Typography sx={{ fontSize: 11, color: "text.disabled" }} noWrap>
            {doc.summary}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
